import axios from 'axios';
import { getErrorMessage } from '../utils/helpers';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001/api/v1';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => response,
  (error) => {
    // A missing Authorization header is answered by HTTPBearer with 403
    // "Not authenticated"; only an invalid token is a 401. Both mean the
    // session is gone, and handling just the 401 left the app sitting on a
    // dead session firing unauthenticated calls that never redirect to login.
    //
    // Matched on that exact detail so a real permission 403 ("Super Admin
    // access required") still surfaces as an error instead of logging the
    // user out. Read before the normalization below rewrites `detail`.
    const sessionExpired =
      error.response?.status === 401 ||
      (error.response?.status === 403 &&
        error.response?.data?.detail === 'Not authenticated');

    if (sessionExpired) {
      localStorage.removeItem('token');
      // Remember where the user was so they land back there after re-login
      // (this is a full page reload, so React Router state would be lost).
      try {
        const path = window.location.pathname + window.location.search;
        if (
          path.startsWith('/') &&
          !path.startsWith('//') &&
          path !== '/' &&
          !path.startsWith('/login') &&
          !path.startsWith('/register')
        ) {
          sessionStorage.setItem('returnTo', path);
        }
      } catch (e) {
        // sessionStorage unavailable — ignore
      }
      window.location.href = '/login';
    }

    // Normalize the error so every call site gets a specific message instead of a
    // generic "Request failed with status code 422". FastAPI returns validation
    // errors as an array under `detail`; collapse it to a readable string and
    // expose it consistently on both `error.message` and `error.response.data.detail`
    // (the latter is rendered directly in some components and must stay a string).
    const friendlyMessage = getErrorMessage(error, error.message);
    error.message = friendlyMessage;
    if (error.response?.data && typeof error.response.data === 'object') {
      error.response.data.detail = friendlyMessage;
    }

    return Promise.reject(error);
  }
);

export default api;